import tenantTheme from '@theme';
import { Pagination, Table } from 'antd';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetLocation, useRouteNavigate } from '../../../hooks';
import { convertQueryObjToString, mapQueryStringToFilterObject } from '../../../utility/urlQuery';
import { getPagerPageSize } from '../../../utility/utility';
import { EmptyListing, NetworkError } from '../../svg';
import EmptyState from '../EmptyState/EmptyState';
import Card from '../cards/Card';
import SelectInput from '../select/select';
import { TableList, TablePagination } from './style';
import cx from 'clsx';

const DataTable = (props) => {
  const {
    className,
    columns,
    data = [],
    pagination,
    showPagination,
    fetchData,
    noUrlPush,
    paginationOnBottom = false,
    loading,
    skeletonLoading,
    pageSize,
    onErrorRetry = () => {},
    tableClass,
    error,
    rowSelection,
    tabList,
    onChangeTab = () => {},
    activeTab,
    emptyState,
    disableTabsOnLoading = false,
    tableStyle,
    // Anchor for a walk-through (tap-tap) step. Kept as a named prop so the id lands on
    // this one wrapper: passing plain `id` leaks through `...rest` onto both the Card and
    // the inner antd table, and the duplicate makes the spotlight resolve to whichever
    // node comes first in the DOM.
    tourId,
    // TruLeads rows-per-page (LEG/86eytgf2v): opt-in { value, options, onChange, label }.
    // Destructured out so it never leaks via `...rest` onto Card/Table below; absent, the
    // footer is byte-identical to before.
    rowsPerPage,
    // Content above the rows inside the table (antd Table `title`). Named so it never reaches the Card via `...rest`.
    tableTitle,
    ...rest
  } = props;

  const navigate = useRouteNavigate();
  const { t, i18n } = useTranslation();
  const location = useGetLocation();
  // Translation Mapping for the titles
  const translateTitlesRecursive = (item) => {
    // if (item.title) {
    //   item.title = t(item.title);
    // } else if (item.label) {
    //   item.label = t(item.label);
    // }
    if (item.children && Array.isArray(item.children)) {
      item.children == item.children.map(translateTitlesRecursive);
    }
    return item;
  };

  const translatedColumns = useMemo(
    () => (columns ? columns.map((item) => translateTitlesRecursive(item)) : []),
    [columns, i18n.language],
  );

  const onShowSizeChange = (current, pageSize) => {
    fetchData({ current, pageSize });
  };

  const onHandleChange = (current, pageSize) => {
    if (noUrlPush) {
      fetchData({ page: current });
    } else {
      let searchParam = location.search;
      const { queryObj } = mapQueryStringToFilterObject(location.search);
      queryObj.page = current;
      // if (!!sorts) {
      //   Object.keys(queryObj).forEach(item => {
      //     if (item.includes('sort')) {
      //       delete queryObj[item];
      //     }
      //     queryObj['sorts[]'] = sorts;
      //   });
      // }
      searchParam = convertQueryObjToString({ ...queryObj });
      navigate({
        pathname: location.pathname,
        search: searchParam,
      });
    }
  };

  const renderPagination = () => {
    const showPager = !!(pagination && data.length && pagination?.totalPages > 1);
    return (
      <TablePagination className={rowsPerPage ? cx('has-rows-per-page', !showPager && 'no-pager') : undefined}>
        {rowsPerPage && (
          <div className="rows-per-page">
            <SelectInput
              label={rowsPerPage.label}
              horizontal
              groupStyle={{ minWidth: 'auto' }}
              value={rowsPerPage.value}
              options={(rowsPerPage.options || []).map((option) => ({ id: option, name: `${option}` }))}
              onChange={rowsPerPage.onChange}
              getPopupContainer={false}
            />
          </div>
        )}
        {showPager ? (
          <Pagination
            onChange={onHandleChange}
            showSizeChanger={false}
            // onShowSizeChange={onShowSizeChange}
            pageSize={getPagerPageSize(pagination, pageSize || 20)}
            defaultCurrent={pagination?.current}
            current={pagination?.current}
            total={pagination?.totalCount}
          />
        ) : null}
      </TablePagination>
    );
  };

  const tablePagination = (pageSize) => {
    if (!pagination && showPagination && data.length) {
      return pageSize
        ? {
            pageSize: pageSize,
          }
        : true;
    }
    return false;
  };

  const renderEmptyState = () => {
    if (loading) return null;

    if (emptyState) {
      return (
        <EmptyState
          type="table"
          hideRetryButton
          message={emptyState.subtitle}
          title={emptyState.title}
          button={emptyState.button}
          illustration={<EmptyListing color={tenantTheme['primary-light-2']} />}
        />
      );
    }
    return <EmptyState type="table" hideRetryButton />;
  };

  return (
    <div>
      <TableList className={tableClass || 'mb-16'}>
        <div className="table-responsive">
          <Card
            id={tourId}
            tabList={tabList}
            onTabChange={onChangeTab}
            activeTabKey={activeTab}
            tabProps={{ size: 'middle', disabled: disableTabsOnLoading ? loading : false }}
            defaultActiveTabKey={Number(tabList?.[0]?.key)}
            style={tableStyle}
            {...rest}
          >
            {error ? (
              <EmptyState
                illustration={<NetworkError />}
                title={t('Error')}
                message={error}
                onClick={() => {
                  onErrorRetry();
                }}
                buttonLoading={loading}
              />
            ) : (
              <Table
                className={className}
                pagination={tablePagination(pageSize)}
                dataSource={data}
                columns={columns}
                loading={skeletonLoading || loading}
                rowClassName={cx(data.length ? 'tableHoverGradient' : '', (record) =>
                  record.hidden ? 'hidden-row' : record?.selected && 'no-highlight-row',
                )}
                locale={{
                  emptyText: renderEmptyState(),
                }}
                rowSelection={rowSelection}
                title={tableTitle}
                {...rest}
                scroll={{
                  x: 'max-content',
                }}
              />
            )}
          </Card>
        </div>
      </TableList>
      {/* TruLeads rows-per-page (LEG/86eytgf2v): the footer must stay visible even when the
          current page size leaves only one page, otherwise a user who picks a large rows-per-page
          value with a small result set has no control left to switch back to a smaller one. The
          numeric pager itself still hides on a single page (see the totalPages > 1 check above). */}
      {paginationOnBottom && !error && data.length && (pagination?.totalPages > 1 || rowsPerPage)
        ? renderPagination()
        : null}
    </div>
  );
};

DataTable.propTypes = {
  className: PropTypes.string,
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  pagination: PropTypes.object,
  fetchData: PropTypes.func,
  noUrlPush: PropTypes.bool,
  paginationOnBottom: PropTypes.bool,
  loading: PropTypes.bool,
  skeletonLoading: PropTypes.bool,
  rowsPerPage: PropTypes.shape({
    value: PropTypes.number,
    options: PropTypes.arrayOf(PropTypes.number),
    onChange: PropTypes.func,
    label: PropTypes.string,
  }),
};

export default DataTable;
